import {AfterContentInit, ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {DcpResourceData} from '@app/model/dcp-resource-data';
import {DcpSupportData} from '@app/model/dcp-support.data';
import {DcpNetNewTermsConditions} from '@app/model/dcp-terms-and-conditions';
import {EddlDownloadAttemptEvent} from '@app/services/analytics/EDDL/events/eddl-download-attempt-event';
import {EddlDownloadSuccessEvent} from '@app/services/analytics/EDDL/events/eddl-download-success-event';
import {TermsConditionsService} from '@app/services/terms-conditions.service';
import {CanonicalId, JsonService, TypeValuePair} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {EventService, Product} from '@spartacus/core';
import {CurrentProductService} from '@spartacus/storefront';
import {Observable} from 'rxjs';
import {DcpProductUtilitiesService} from '../../services/dcp-product-utilities.service';
import {NavigationLink} from '../navigation-bar/navigation-link';
import {ProductDetailsNavigationLinkService} from '../navigation-bar/product-details-navigation-link.service';
import {ProductDetailsSectionComponent} from '../product-details-section/product-details-section.component';
import {first} from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'dcp-product-resources',
  templateUrl: './product-resources.component.html',
  styleUrls: ['./product-resources.component.scss']
})
export class ProductResourcesComponent extends ProductDetailsSectionComponent implements OnInit, OnDestroy, AfterContentInit {

  product$: Observable<Product>;
  product: Product;
  entryLimit = 4;
  resourcesExtended = false;
  resources: DcpResourceData[] = [];
  support: DcpSupportData;
  termsConditionsProductEntries = [];
  terms: string;
  privacy: string;
  navLink: NavigationLink = {
    headline: 'productDetails.resources',
    id: 'resources-section'
  };

  constructor(
    dcpProductUtilitiesService: DcpProductUtilitiesService,
    protected currentProductService: CurrentProductService,
    protected changeDetection: ChangeDetectorRef,
    protected productDetailsNavigationLinkService: ProductDetailsNavigationLinkService,
    private termsConditionsService: TermsConditionsService,
    private jsonService: JsonService,
    private eventService: EventService
  ) {
    super(currentProductService, changeDetection, productDetailsNavigationLinkService, dcpProductUtilitiesService);
  }

  get limitedResources(): DcpResourceData[] {
    return this.resourcesExtended ? this.resources : this.resources.slice(0, this.entryLimit);
  }

  isVisible(): boolean {
    return !!this.product && ((!!this.resources && this.resources.length > 0) || !!this.terms || !!this.privacy
      || (!!this.support && (!!this.support.knowledgebase || !!this.support.email)) ||
      (!!this.termsConditionsProductEntries && this.termsConditionsProductEntries.length > 0));
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  ngAfterContentInit(): void {
    super.ngAfterContentInit();
  }

  toggleResourcesWithKeyboard(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.toggleResources();
    }
  }

  toggleResources(): void {
    this.resourcesExtended = !this.resourcesExtended;
  }

  prepareData(): void {
    if (!!this.product?.data) {
      this.resources = this.findAttribute('resources') as DcpResourceData[] || [];
      this.privacy = this.findAttribute('privacy') as string;
      this.support = this.findAttribute('support') as DcpSupportData;
      this.terms = this.findAttribute('terms') as string;
      this.termsConditionsProductEntries = this.termsConditionsService.createTermsConditionsEntriesFromProduct(this.product);
      if (!this.dcpProductUtilitiesService.isSapProduct(this.product)) {
        this.setPartnerProductTerms();
      }

    }
  }

  setPartnerProductTerms(): void {
    this.fetchTermsAndCondition(this.dcpProductUtilitiesService.getCanonicalId(this.product))
      .pipe(
        untilDestroyed(this),
        first()
      ).subscribe((netNewTermsConditions: DcpNetNewTermsConditions[]) => {
      if (netNewTermsConditions?.[0]?.urlTermsConds?.length) {
        this.terms = netNewTermsConditions?.[0]?.urlTermsConds;
        this.changeDetection.detectChanges();
      }
      if (!this.terms?.length) {
        this.fetchTermsAndCondition(this.dcpProductUtilitiesService.getCanonicalId(this.termsConditionsProductEntries[0].product))
          .pipe(
            untilDestroyed(this),
            first()
          ).subscribe((netNewTermsConditions: DcpNetNewTermsConditions[]) => {
          this.terms = netNewTermsConditions[0].urlTermsConds || this.terms;
          this.changeDetection.detectChanges();
        });
      }
      this.terms = netNewTermsConditions?.[0]?.urlTermsConds || this.terms;
    });
  }

  private fetchTermsAndCondition(canonicalId: CanonicalId): Observable<DcpNetNewTermsConditions[] | undefined> {
    return this.termsConditionsService.fetchTermsConditionsByCanonicalId(
      canonicalId,
      {salesOrg: undefined}
    );
  }

  findAttribute(entryType: string): any {
    let attr: any;
    const rawAttr: TypeValuePair = this.product?.data.find(entry => entry.type === entryType);
    if (rawAttr) {
      attr = this.jsonService.parseSafely(rawAttr.value);
    }
    return attr;
  }

  public triggerEddlDownloadAttemptEvent(clickEvent): void {
    if (clickEvent.target.href?.includes('.pdf')) {
      const event = new EddlDownloadAttemptEvent();
      event.url = clickEvent.target.href;
      setTimeout(() => this.eventService.dispatch(event));
    }
  }

  public triggerEddlDownloadSuccessEvent(clickEvent): void {
    if (clickEvent.target.href?.includes('.pdf')) {
      const event = new EddlDownloadSuccessEvent();
      event.url = clickEvent.target.href;
      setTimeout(() => this.eventService.dispatch(event));
    }
  }
}
